import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import {
  createProfileService,
  patchProfileDisplayTheme,
} from '@/features/profile/profileService';
import {
  profileQueryKey,
  type ProfileData,
} from '@/features/profile/profileTypes';

import type { ResolvedTheme, ThemeSetting } from './themeTypes';
import {
  applyThemeToDocument,
  getInitialThemeSetting,
  getSystemResolvedTheme,
  resolveTheme,
  writeStoredThemeSetting,
} from './themeScript';

interface ServerThemeSyncMarker {
  theme: ThemeSetting;
  userId: string;
}

export interface ThemeContextValue {
  clearRouteThemeOverride: () => void;
  isSavingTheme: boolean;
  lastSavedTheme: ThemeSetting | null;
  resolvedTheme: ResolvedTheme;
  routeThemeOverride: ResolvedTheme | null;
  setRouteThemeOverride: (theme: ResolvedTheme | null) => void;
  setThemeSetting: (theme: ThemeSetting) => void;
  themeSaveError: ApiError | null;
  themeSetting: ThemeSetting;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
);

interface ThemeProviderProps {
  children: ReactNode;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { getIdToken, loading, user } = useAuth();
  const queryClient = useQueryClient();
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? null;
  const [themeSetting, setThemeSettingState] = useState<ThemeSetting>(() =>
    getInitialThemeSetting(),
  );
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    getSystemResolvedTheme(),
  );
  const [routeThemeOverride, setRouteThemeOverrideState] =
    useState<ResolvedTheme | null>(null);
  const [lastSavedTheme, setLastSavedTheme] = useState<ThemeSetting | null>(
    null,
  );
  const lastServerSyncRef = useRef<ServerThemeSyncMarker | null>(null);

  const createClient = useCallback(() => {
    if (!user) {
      throw createDataError('You must be signed in to save theme settings.');
    }

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
    });
  }, [getIdToken, user]);

  const profileQuery = useQuery({
    enabled: !loading && Boolean(user) && Boolean(kuduUserId),
    queryFn: async () => {
      if (!kuduUserId) {
        throw createDataError('You must be signed in to load theme settings.');
      }

      return createProfileService(createClient()).getProfile({
        userId: kuduUserId,
      });
    },
    queryKey: profileQueryKey(kuduUserId),
    retry: false,
    staleTime: 30_000,
  });

  const saveThemeMutation = useMutation<ProfileData | null, ApiError, ThemeSetting>({
    mutationFn: async (nextTheme) => {
      if (!kuduUserId) {
        return null;
      }

      return createProfileService(createClient()).updateDisplayTheme({
        theme: nextTheme,
        userId: kuduUserId,
      });
    },
    onMutate: () => {
      setLastSavedTheme(null);
    },
    onSuccess: (updatedProfile, submittedTheme) => {
      if (!kuduUserId) {
        return;
      }

      queryClient.setQueryData(
        profileQueryKey(kuduUserId),
        (currentProfile: ProfileData | undefined) => {
          if (updatedProfile && currentProfile) {
            return {
              ...currentProfile,
              rawSettings: updatedProfile.rawSettings,
              settings: updatedProfile.settings,
            };
          }

          return (
            updatedProfile ??
            patchProfileDisplayTheme(currentProfile, submittedTheme)
          );
        },
      );
      setLastSavedTheme(submittedTheme);
    },
  });

  const resolvedTheme = useMemo(
    () => resolveTheme(themeSetting, systemTheme),
    [systemTheme, themeSetting],
  );
  const appliedTheme = routeThemeOverride ?? resolvedTheme;

  useEffect(() => {
    applyThemeToDocument(appliedTheme);
  }, [appliedTheme]);

  useEffect(() => {
    if (themeSetting !== 'system' || typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    if (!mediaQuery) {
      setSystemTheme('light');
      return undefined;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [themeSetting]);

  useEffect(() => {
    lastServerSyncRef.current = null;
  }, [kuduUserId]);

  useEffect(() => {
    const serverTheme = profileQuery.data?.settings.displayTheme ?? null;

    if (!kuduUserId || !serverTheme) {
      return;
    }

    const lastServerSync = lastServerSyncRef.current;

    if (
      lastServerSync?.userId === kuduUserId &&
      lastServerSync.theme === serverTheme
    ) {
      return;
    }

    lastServerSyncRef.current = {
      theme: serverTheme,
      userId: kuduUserId,
    };
    setThemeSettingState(serverTheme);
    writeStoredThemeSetting(serverTheme);
  }, [kuduUserId, profileQuery.data?.settings.displayTheme]);

  const setThemeSetting = useCallback(
    (nextTheme: ThemeSetting) => {
      setThemeSettingState(nextTheme);
      writeStoredThemeSetting(nextTheme);
      setLastSavedTheme(null);

      if (kuduUserId) {
        saveThemeMutation.mutate(nextTheme);
      }
    },
    [kuduUserId, saveThemeMutation],
  );

  const setRouteThemeOverride = useCallback(
    (theme: ResolvedTheme | null) => {
      setRouteThemeOverrideState(theme);
    },
    [],
  );

  const clearRouteThemeOverride = useCallback(() => {
    setRouteThemeOverrideState(null);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      clearRouteThemeOverride,
      isSavingTheme: saveThemeMutation.isPending,
      lastSavedTheme,
      resolvedTheme,
      routeThemeOverride,
      setRouteThemeOverride,
      setThemeSetting,
      themeSaveError: saveThemeMutation.error ?? null,
      themeSetting,
    }),
    [
      clearRouteThemeOverride,
      lastSavedTheme,
      resolvedTheme,
      routeThemeOverride,
      saveThemeMutation.error,
      saveThemeMutation.isPending,
      setRouteThemeOverride,
      setThemeSetting,
      themeSetting,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
